/**
 * Branding come lo restituisce l'API: campi assenti valorizzati a `null`.
 *
 * Non riusa `EventBranding` di `@ecc/shared`, che descrive l'input di
 * creazione con campi opzionali: confonderli porterebbe a controlli sbagliati
 * (`undefined` invece di `null`) proprio dove si decide se mostrare l'immagine.
 */
export type KioskEventBranding = {
  imageAssetId: string | null;
  imageAltText: string | null;
  headline: string | null;
  shortDescription: string | null;
};

/**
 * Configurazione che il kiosk riceve dal server.
 *
 * `marketingConsentText` è `null` quando il testo non è pubblicato nella lingua
 * corrente: in quel caso la casella marketing non va mostrata (§21).
 */
export type KioskConfig = {
  event: {
    id: string;
    name: string;
    slug: string;
    status: 'DRAFT' | 'ACTIVE' | 'CLOSED' | 'ARCHIVED';
    languages: readonly string[];
    branding: KioskEventBranding;
  };
  language: string;
  /**
   * Materiali richiedibili configurati per questa fiera, con l'etichetta già
   * risolta nella lingua servita. Nessun campo di preselezione: il tipo non lo
   * prevede, quindi il form non ha un modo per spuntarne uno da solo (§23).
   */
  requestableItems: readonly {
    id: string;
    type: 'DOCUMENT' | 'CATALOG' | 'PRODUCT_INFO';
    label: string;
  }[];
  /** Etichetta della casella di ricontatto; `null` usa quella predefinita. */
  callbackLabel: string | null;
  policy: {
    policyId: string;
    version: string;
    language: string;
    title: string;
    content: string;
    hash: string;
  };
  marketingConsentText: {
    consentTextId: string;
    version: string;
    language: string;
    text: string;
    hash: string;
  } | null;
};

export class KioskNotPairedError extends Error {}

/**
 * Nel browser è attiva una sessione operatore, che ha la precedenza su quella
 * kiosk. Distinto da `KioskNotPairedError` perché la soluzione è diversa:
 * non serve un codice, serve un'altra finestra.
 */
export class OperatorSessionActiveError extends Error {}

export async function fetchKioskConfig(language?: string): Promise<KioskConfig> {
  const query = language ? `?language=${encodeURIComponent(language)}` : '';
  const response = await fetch(`/api/kiosk/config${query}`, { credentials: 'include' });

  if (response.status === 409) {
    throw new OperatorSessionActiveError('Sessione operatore attiva');
  }
  if (response.status === 401 || response.status === 403) {
    throw new KioskNotPairedError('Dispositivo non associato');
  }
  if (!response.ok) {
    throw new Error(`Configurazione non disponibile (HTTP ${response.status})`);
  }

  return (await response.json()) as KioskConfig;
}

export async function pairDevice(token: string): Promise<void> {
  const response = await fetch('/api/kiosk/pair', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) throw new KioskNotPairedError('Codice non valido');
}
